import React from 'react';
import type { Column, FilterOperator } from '../types';
import { FILTER_OPERATORS_BY_TYPE } from '../types';
import { Eye, EyeOff, ArrowUp, ArrowDown, ChevronUp, ChevronDown, Filter, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@t-one-internal/design-system';

interface ColumnConfiguratorProps {
    columns: Column[];
    onChange: (columns: Column[]) => void;
}

const ColumnConfigurator: React.FC<ColumnConfiguratorProps> = ({ columns, onChange }) => {

    // Get valid operators for a column based on its data type
    const getOperatorsForColumn = (col: Column) => {
        const dataType = col.dataType || 'string';
        return FILTER_OPERATORS_BY_TYPE[dataType] || FILTER_OPERATORS_BY_TYPE.string;
    };

    // Get default operator for a column based on its data type
    const getDefaultOperator = (col: Column): FilterOperator => {
        const operators = getOperatorsForColumn(col);
        // For string type, default to 'like' (contains), otherwise use '='
        if (col.dataType === 'string') {
            return 'like';
        }
        return operators[0]?.value || '=';
    };

    const toggleVisibility = (index: number) => {
        const newCols = [...columns];
        newCols[index].visible = !newCols[index].visible;
        onChange(newCols);
    };

    const moveColumn = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === columns.length - 1) return;

        const newCols = [...columns];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        [newCols[index], newCols[targetIndex]] = [newCols[targetIndex], newCols[index]];
        onChange(newCols);
    };

    const updateSort = (index: number) => {
        const newCols = [...columns];
        const current = newCols[index].sort;
        newCols[index].sort = current === 'asc' ? 'desc' : current === 'desc' ? null : 'asc';
        onChange(newCols);
    };

    const addFilter = (index: number) => {
        const newCols = [...columns];
        if (!newCols[index].filterOperator) {
            newCols[index].filterOperator = getDefaultOperator(newCols[index]);
            newCols[index].filter = '';
        }
        onChange(newCols);
    };

    const removeFilter = (index: number) => {
        const newCols = [...columns];
        newCols[index].filter = undefined;
        newCols[index].filterOperator = undefined;
        onChange(newCols);
    };

    const updateFilterValue = (index: number, value: string) => {
        const newCols = [...columns];
        newCols[index].filter = value;
        onChange(newCols);
    };

    const updateFilterOperator = (index: number, op: FilterOperator) => {
        const newCols = [...columns];
        newCols[index].filterOperator = op;
        onChange(newCols);
    };

    // Get appropriate input type based on column data type
    const getInputType = (col: Column): string => {
        switch (col.dataType) {
            case 'integer':
            case 'decimal':
                return 'number';
            case 'date':
                return 'date';
            case 'timestamp':
                return 'datetime-local';
            default:
                return 'text';
        }
    };

    // Get placeholder text based on column data type and operator
    const getPlaceholder = (col: Column): string => {
        if (col.filterOperator === 'in') {
            return 'value1, value2, ...';
        }
        switch (col.dataType) {
            case 'integer':
                return 'Enter number...';
            case 'decimal':
                return 'Enter decimal...';
            case 'date':
            case 'timestamp':
                return '';
            case 'boolean':
                return 'true or false';
            default:
                return 'Enter value...';
        }
    };

    const nonMandatoryColumns = columns.filter(c => !c.isMandatory);
    const allNonMandatoryVisible = nonMandatoryColumns.length > 0 && nonMandatoryColumns.every(c => c.visible);
    const someNonMandatoryVisible = nonMandatoryColumns.some(c => c.visible);

    const handleSelectAll = () => {
        const newCols = columns.map(col => ({
            ...col,
            visible: col.isMandatory ? col.visible : !allNonMandatoryVisible,
        }));
        onChange(newCols);
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Available Columns</h3>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-600 hover:text-gray-800">
                        <input
                            type="checkbox"
                            checked={allNonMandatoryVisible}
                            ref={(el) => { if (el) el.indeterminate = someNonMandatoryVisible && !allNonMandatoryVisible; }}
                            onChange={handleSelectAll}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {allNonMandatoryVisible ? 'Deselect All' : 'Select All'}
                    </label>
                    <span className="text-xs text-gray-500">{columns.filter(c => c.visible).length} visible</span>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden shadow-sm">
                {columns.map((col, idx) => (
                    <div key={col.id} className="flex items-stretch transition-colors bg-white">

                        {/* Full Height Reorder Sidepanel */}
                        <div className="flex flex-col w-12">
                            <button
                                type="button"
                                onClick={() => moveColumn(idx, 'up')}
                                disabled={idx === 0}
                                className="flex-1 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-slate-100 disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-gray-400 transition-all"
                            >
                                <ChevronUp size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => moveColumn(idx, 'down')}
                                disabled={idx === columns.length - 1}
                                className="flex-1 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-slate-100 disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-gray-400 transition-all"
                            >
                                <ChevronDown size={16} />
                            </button>
                        </div>

                        {/* Main Content Info */}
                        <div className="flex-1 flex flex-col py-4 px-4">

                            {/* Top Row */}
                            <div className="flex items-center gap-3">
                                {/* Visibility Toggle - disabled for mandatory columns */}
                                <button
                                    type="button"
                                    onClick={() => !col.isMandatory && toggleVisibility(idx)}
                                    disabled={col.isMandatory}
                                    className={`p-1.5 rounded-md transition-colors ${col.isMandatory
                                        ? 'text-green-600 bg-green-50 cursor-not-allowed opacity-60'
                                        : col.visible
                                            ? 'text-green-600 bg-green-50 hover:bg-green-100'
                                            : 'text-gray-400 bg-gray-100 hover:bg-gray-200'
                                        }`}
                                    title={col.isMandatory ? "Required column" : col.visible ? "Hide Column" : "Show Column"}
                                >
                                    {col.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                                </button>

                                {/* Column Info */}
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`font-medium text-sm ${col.visible ? 'text-gray-900' : 'text-gray-400'}`}
                                        title={col.label}
                                    >
                                        {col.label}
                                    </span>
                                    <span className="text-[10px] uppercase bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded border border-gray-200 shrink-0">
                                        {col.type}
                                    </span>
                                </div>

                                {/* Spacer */}
                                <div className="flex-1" />

                                {/* Sort Button - only show if sortable */}
                                {col.sortable !== false && (
                                    <button
                                        type="button"
                                        onClick={() => updateSort(idx)}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors border ${col.sort
                                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                                            : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        Sort
                                        {col.sort === 'asc' && <ArrowUp size={12} />}
                                        {col.sort === 'desc' && <ArrowDown size={12} />}
                                    </button>
                                )}

                                {/* Filter Toggle Button - only show if filterable */}
                                {col.filterable !== false && (
                                    <button
                                        type="button"
                                        onClick={() => addFilter(idx)}
                                        className={`p-1.5 rounded-md transition-colors border ${col.filterOperator
                                            ? 'bg-blue-50 text-blue-600 border-blue-200'
                                            : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300 hover:text-gray-600'
                                            }`}
                                        title="Add Filter"
                                    >
                                        <Filter size={16} />
                                    </button>
                                )}
                            </div>

                            {/* Filter Row - with data type specific operators */}
                            {col.filterOperator && (
                                <div className="flex items-center gap-2 mt-3 animate-in fade-in slide-in-from-top-1">
                                    <Select
                                        value={col.filterOperator}
                                        onValueChange={(val: string) => updateFilterOperator(idx, val as FilterOperator)}
                                    >
                                        <SelectTrigger className="w-40 h-8 text-xs">
                                            <SelectValue placeholder="Operator" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {getOperatorsForColumn(col).map(op => (
                                                <SelectItem key={op.value} value={op.value}>
                                                    {op.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <input
                                        type={getInputType(col)}
                                        value={col.filter || ''}
                                        onChange={(e) => updateFilterValue(idx, e.target.value)}
                                        placeholder={getPlaceholder(col)}
                                        step={col.dataType === 'decimal' ? '0.01' : undefined}
                                        className="flex-1 min-w-[100px] px-3 py-1.5 h-8 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />

                                    <button
                                        type="button"
                                        onClick={() => removeFilter(idx)}
                                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                        title="Remove Filter"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ColumnConfigurator;
